import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Flex,
  Spacer,
  Icon,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  Grid,
  GridItem,
  Avatar,
  IconButton,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  InputGroup,
  InputLeftAddon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiUser,
  FiPlus,
  FiEdit,
  FiLock,
  FiUnlock,
  FiFileText,
  FiUpload,
  FiDownload,
  FiTrash2,
  FiEye,
  FiStar,
  FiBookOpen,
  FiActivity,
  FiAlertCircle,
  FiCheckCircle,
  FiUserCheck,
  FiUserPlus,
  FiTruck,
} from 'react-icons/fi';
import { useFindOne, useUser, useFindMany, useAction, useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';
import { hasAnyRole, isInstructor, isScheduler } from '../lib/roleUtils';
import FormChecklist from '../components/FormChecklist';
import StudentEvalQrGenerator from '../components/StudentEvalQrGenerator';

// Add this function before the ShiftDetails component
const getShiftTypeIcon = (shiftType) => {
  switch (shiftType) {
    case 'field':
      return '🚑'; // Ambulance emoji for field shifts
    case 'hospital':
      return '🏥'; // Hospital emoji for hospital shifts
    case 'lab':
      return '🧪'; // Test tube emoji for lab shifts
    case 'field_internship':
      return '🏁'; // Checkered flag emoji for field internship
    default:
      return '📋'; // Clipboard emoji as fallback
  }
};

const PreceptorSelector = ({ shift, user, canEdit, onPreceptorChange }) => {
  const toast = useToast();
  const { isOpen: isRequestOpen, onOpen: onRequestOpen, onClose: onRequestClose } = useDisclosure();
  const [selectedPreceptor, setSelectedPreceptor] = useState(shift.preceptor?.id || '');
  const [newPreceptorRequest, setNewPreceptorRequest] = useState({
    firstName: '',
    lastName: '',
    email: '',
    department: '',
    justification: ''
  });

  // Fetch institutional preceptors who work at this clinical site
  // Include preceptors with no department (available anywhere) or matching location
  const [{ data: preceptors, fetching: preceptorsFetching }, refetchPreceptors] = useFindMany(
    api.preceptor,
    {
      filter: { 
        AND: [
          { institution: { equals: shift.institution } },
          { isActive: { equals: true } },
          {
            OR: [
              // Preceptors who work at this specific location
              { department: { equals: shift.location } },
              // Preceptors who don't have a specific department listed (available anywhere)
              { department: { equals: null } },
              { department: { equals: "" } }
            ]
          }
        ]
      },
      select: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        department: true,
        primaryCertification: true,
        userId: true,
      },
      sort: [{ lastName: 'Ascending' }, { firstName: 'Ascending' }]
    }
  );

  // State for requesting new preceptor
  const [requestingPreceptor, setRequestingPreceptor] = useState(false);
  const [requestResult, setRequestResult] = useState(null);
  const [requestError, setRequestError] = useState(null);

  const handlePreceptorChange = async (preceptorId) => {
    if (preceptorId === 'request_new') {
      onRequestOpen();
      return;
    }
    
    try {
      await onPreceptorChange(preceptorId);
      setSelectedPreceptor(preceptorId);
    } catch (error) {
      console.error('Error updating preceptor:', error);
    }
  };

  const handleRequestNewPreceptor = async () => {
    try {
      setRequestingPreceptor(true);
      setRequestError(null);
      
      // Check if the global action exists
      if (api.requestNewPreceptor) {
        const result = await api.requestNewPreceptor({
          shiftId: shift.id,
          requestedBy: user.id,
          preceptorInfo: newPreceptorRequest,
          institution: shift.institution,
          justification: newPreceptorRequest.justification
        });
        setRequestResult(result);
      } else {
        // Fallback: just show a message that the request was submitted
        console.log('Preceptor request submitted:', {
          shiftId: shift.id,
          requestedBy: user.id,
          preceptorInfo: newPreceptorRequest,
          institution: shift.institution
        });
        setRequestResult({ message: 'Request submitted successfully' });
      }
      
      toast({
        title: 'Preceptor request submitted',
        description: 'Your request for a new preceptor has been sent to the scheduling team.',
        status: 'success',
        duration: 5000,
      });
      
      // Reset form and close modal
      setNewPreceptorRequest({
        firstName: '',
        lastName: '',
        email: '',
        department: '',
        justification: ''
      });
      onRequestClose();
      
    } catch (error) {
      console.error('Error requesting new preceptor:', error);
      setRequestError(error);
      toast({
        title: 'Error submitting request',
        description: error.message || 'Failed to submit preceptor request',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setRequestingPreceptor(false);
    }
  };

  return (
    <>
      <HStack spacing={3} alignItems="center">
        {canEdit && !shift.locked ? (
          <Select 
            size="sm" 
            value={selectedPreceptor} 
            onChange={(e) => handlePreceptorChange(e.target.value)}
            placeholder={shift.preceptor ? `${shift.preceptor.firstName} ${shift.preceptor.lastName}` : "Select Preceptor..."}
            maxW="200px"
            variant="outline"
          >
            {preceptors?.map((preceptor) => (
              <option key={preceptor.id} value={preceptor.id}>
                {preceptor.firstName} {preceptor.lastName}
              </option>
            ))}
            <option value="request_new">Request New...</option>
          </Select>
        ) : (
          <Text fontSize="sm" color="gray.700" fontWeight="medium">
            {shift.preceptor ? (
              `Preceptor: ${shift.preceptor.firstName} ${shift.preceptor.lastName}`
            ) : (
              <Text as="span" fontStyle="italic" color="gray.500">Preceptor: Not assigned</Text>
            )}
          </Text>
        )}
      </HStack>

      {/* Request New Preceptor Modal */}
      <Modal isOpen={isRequestOpen} onClose={onRequestClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Request New Preceptor</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Use this form to request a new preceptor who is not currently in the system. 
                  Your request will be sent to the scheduling team for review.
                </Text>
              </Alert>

              <SimpleGrid columns={2} spacing={4} width="100%">
                <FormControl isRequired>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={newPreceptorRequest.firstName}
                    onChange={(e) => setNewPreceptorRequest({
                      ...newPreceptorRequest,
                      firstName: e.target.value
                    })}
                    placeholder="Enter first name"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={newPreceptorRequest.lastName}
                    onChange={(e) => setNewPreceptorRequest({
                      ...newPreceptorRequest,
                      lastName: e.target.value
                    })}
                    placeholder="Enter last name"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  value={newPreceptorRequest.email}
                  onChange={(e) => setNewPreceptorRequest({
                    ...newPreceptorRequest,
                    email: e.target.value
                  })}
                  placeholder="Enter email address"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Department/Unit</FormLabel>
                <Input
                  value={newPreceptorRequest.department}
                  onChange={(e) => setNewPreceptorRequest({
                    ...newPreceptorRequest,
                    department: e.target.value
                  })}
                  placeholder="e.g., Emergency, ICU, Ambulance"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Justification</FormLabel>
                <Textarea
                  value={newPreceptorRequest.justification}
                  onChange={(e) => setNewPreceptorRequest({
                    ...newPreceptorRequest,
                    justification: e.target.value
                  })}
                  placeholder="Please explain why you need this specific preceptor and provide any relevant details..."
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRequestClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleRequestNewPreceptor}
              isLoading={requestingPreceptor}
              isDisabled={!newPreceptorRequest.firstName || !newPreceptorRequest.lastName || !newPreceptorRequest.email || !newPreceptorRequest.justification}
            >
              Submit Request
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const ShiftDetails = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isNewEncounterOpen, onOpen: onNewEncounterOpen, onClose: onNewEncounterClose } = useDisclosure();
  const { isOpen: isEditShiftOpen, onOpen: onEditShiftOpen, onClose: onEditShiftClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLockHovered, setIsLockHovered] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [newEncounterData, setNewEncounterData] = useState({
    patientAge: '',
    patientBiologicalSex: '',
    patientGender: '',
    patientPronouns: '',
    ageUnknown: false,
    biologicalSexUnknown: false,
    chiefComplaint: '',
    primaryImpression: ''
  });
  
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch shift data
  const [{ data: shift, fetching: shiftFetching, error: shiftError }, refetchShift] = useFindOne(api.shift, shiftId, {
    select: {
      id: true,
      shiftDate: true,
      endTime: true,
      location: true,
      unit: true,
      shiftType: true,
      status: true,
      locked: true,
      institution: true,
      cohort: true,
      maxStudents: true,
      isOpenEnrollment: true,
      requiresPreceptorApproval: true,
      objectives: { markdown: true },
      notes: { markdown: true },
      preceptorNotes: { markdown: true },
      reflectionNotes: { markdown: true },
      student: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        institution: true,
        cohort: true,
        institutionRole: true,
      },
      preceptor: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        institution: true,
      },
      createdBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
      },
      encounters: {
        edges: {
          node: {
            id: true,
            chiefComplaint: { markdown: true },
            primaryImpression: true,
            reviewStatus: true,
            encounterDate: true,
            patientAge: true,
            patientGender: true,
            createdAt: true,
          },
        },
      },
    },
  });

  // Get current user
  const user = useUser(api);

  // Fetch patient encounters for this shift
  const [{ data: encounters, fetching: encountersFetching, error: encountersError }, refetchEncounters] = useFindMany(
    api.patientEncounter,
    {
      filter: { shiftId: { equals: shiftId } },
      select: {
        id: true,
        chiefComplaint: { markdown: true },
        primaryImpression: true,
        secondaryImpression: true,
        reviewStatus: true,
        encounterDate: true,
        patientAge: true,
        patientGender: true,
        patientCriticality: true,
        createdAt: true,
        updatedAt: true,
      },
    }
  );

  // Fetch shift documentation for the current student (for form checklist)
  const [{ data: shiftDocs, fetching: shiftDocsFetching }] = useFindMany(
    api.shiftDocumentation,
    {
      filter: { shiftId: { equals: shiftId } },
      select: {
        id: true,
        studentId: true,
        formChecklist: true,
        appliedShiftPack: { id: true, name: true },
      },
      first: 50,
    }
  );

  // Fetch form submissions for this shift (for Documents tab)
  const [{ data: formSubmissions, fetching: formSubmissionsFetching }, refetchFormSubmissions] = useFindMany(
    api.formSubmission,
    {
      filter: { shift: { equals: shiftId } },
      select: {
        id: true,
        submissionType: true,
        status: true,
        submittedAt: true,
        variances: true,
        phiDetected: true,
        institutionForm: { id: true, name: true, formCategory: true },
        student: { id: true, firstName: true, lastName: true },
      },
      sort: { submittedAt: "Descending" },
      first: 100,
    }
  );

  // Actions
  const [{ data: createEncounterResult, fetching: creatingEncounter, error: createError }, createEncounter] = useAction(
    api.patientEncounter.create
  );
  const [{ data: updateShiftResult, fetching: updatingShift, error: updateError }, updateShift] = useAction(
    api.shift.update
  );
  const [{ data: deleteShiftResult, fetching: deletingShift, error: deleteError }, deleteShift] = useAction(
    api.shift.delete
  );

  // Check permissions
  const canEdit = user && (
    hasAnyRole(user?.roles, ['admin', 'system-admin']) ||
    user.institutionRole === 'administrator' ||
    isInstructor(user) ||
    isScheduler(user)
  );

  const canViewShift = user && (
    hasAnyRole(user?.roles, ['admin', 'system-admin']) ||
    (shift?.student?.id === user.id) ||
    (shift?.preceptor?.id === user.id) ||
    (shift?.institution === user.institution && (user.institutionRole === 'administrator' || isInstructor(user) || isScheduler(user)))
  );

  const canLockUnlock = user && (
    hasAnyRole(user?.roles, ['admin', 'system-admin']) ||
    user.institutionRole === 'administrator' ||
    isInstructor(user)
  );

  // Handle preceptor change
  const handlePreceptorChange = async (preceptorId) => {
    try {
      await updateShift({
        id: shift.id,
        preceptor: preceptorId ? { _link: preceptorId } : null,
      });
      
      // Refetch the shift data to get the updated state
      await refetchShift();
      
      toast({
        title: 'Preceptor updated',
        description: 'The shift preceptor has been successfully updated.',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error updating preceptor:', error);
      toast({
        title: 'Error updating preceptor',
        description: error.message || 'Failed to update shift preceptor',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle delete shift
  const handleDeleteShift = async () => {
    try {
      await deleteShift({ id: shift.id });
      toast({
        title: 'Shift deleted successfully',
        status: 'success',
        duration: 3000,
      });
      navigate('/shifts');
    } catch (error) {
      console.error('Delete shift error:', error);
      toast({
        title: 'Error deleting shift',
        description: error.message || 'Failed to delete shift',
        status: 'error',
        duration: 5000,
      });
    }
    onDeleteClose();
  };

  // Handle loading states
  if (shiftFetching) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading shift details...</Text>
        </VStack>
      </Container>
    );
  }

  // Handle errors
  if (shiftError || !shift) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Error loading shift: {shiftError?.message || 'Shift not found'}
        </Alert>
      </Container>
    );
  }

  // Check access control
  if (!canViewShift) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="warning">
          <AlertIcon />
          You don't have permission to view this shift.
        </Alert>
      </Container>
    );
  }

  // Handle shift lock/unlock
  const handleLockToggle = async () => {
    try {
      const currentLockState = shift.locked;
      const newLockedState = !currentLockState;
      
      console.log('Lock toggle:', { 
        current: currentLockState, 
        new: newLockedState, 
        shiftId: shift.id,
        buttonState: shift.locked ? 'unlock' : 'lock'
      });
      
      const result = await updateShift({
        id: shift.id,
        locked: newLockedState,
      });
      
      console.log('Update result:', result);
      
      // Refetch the shift data to get the updated state
      await refetchShift();
      
      toast({
        title: newLockedState ? 'Shift locked' : 'Shift unlocked',
        description: newLockedState ? 'Students can no longer edit this shift' : 'Students can now edit this shift',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Lock toggle error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      toast({
        title: 'Error updating shift',
        description: error.message || 'Failed to update shift lock status',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle new patient encounter
  const handleNewEncounter = async () => {
    try {
      // Validation
      if (!newEncounterData.ageUnknown && !newEncounterData.patientAge) {
        toast({
          title: 'Validation Error',
          description: 'Please provide patient age or mark as unknown',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      
      if (!newEncounterData.biologicalSexUnknown && !newEncounterData.patientBiologicalSex) {
        toast({
          title: 'Validation Error',
          description: 'Please provide patient biological sex or mark as unknown',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      
      const encounterData = {
        patientAge: newEncounterData.ageUnknown ? 0 : parseInt(newEncounterData.patientAge) || 0,
        patientBiologicalSex: newEncounterData.biologicalSexUnknown ? 'unknown' : newEncounterData.patientBiologicalSex,
        patientGender: newEncounterData.patientGender,
        patientPronouns: newEncounterData.patientPronouns,
        chiefComplaint: { markdown: newEncounterData.chiefComplaint || 'Not specified' },
        primaryImpression: newEncounterData.primaryImpression || 'other_medical',
        encounterDate: new Date().toISOString(),
        reviewStatus: 'draft',
        shift: { _link: shift.id },
        student: { _link: shift.student.id },
        submittedBy: { _link: user.id },
        preceptor: shift.preceptor ? { _link: shift.preceptor.id } : undefined,
        institution: shift.institution,
        cohort: shift.cohort,
        // Required fields with default values
        allergies: { markdown: 'Not assessed' },
        cardiacAssessment: { markdown: 'Not assessed' },
        chestAssessment: { markdown: 'Not assessed' },
        differentialDiagnosis: { markdown: 'Not assessed' },
        eventsPrior: { markdown: 'Not assessed' },
        extremitiesAssessment: { markdown: 'Not assessed' },
        fieldImpressions: { markdown: 'Not assessed' },
        heentAssessment: { markdown: 'Not assessed' },
        historyPresentIllness: { markdown: 'Not assessed' },
        lastOralIntake: { markdown: 'Not assessed' },
        lungSounds: { markdown: 'Not assessed' },
        medications: { markdown: 'Not assessed' },
        mentalStatus: { markdown: 'Not assessed' },
        neckBackAssessment: { markdown: 'Not assessed' },
        neurologicalAssessment: { markdown: 'Not assessed' },
        onsetTiming: { markdown: 'Not assessed' },
        pastMedicalHistory: { markdown: 'Not assessed' },
        provocationPalliation: { markdown: 'Not assessed' },
        quality: { markdown: 'Not assessed' },
        qaDocumentationCriteria: { markdown: 'Not assessed' },
        radiation: { markdown: 'Not assessed' },
        reassessmentResponse: { markdown: 'Not assessed' },
        severity: { markdown: 'Not assessed' },
        skinAssessment: { markdown: 'Not assessed' },
        timing: { markdown: 'Not assessed' },
        transportDisposition: { markdown: 'Not assessed' },
        treatmentInterventions: { markdown: 'Not assessed' },
        abdomenAssessment: { markdown: 'Not assessed' },
        heartSounds: { markdown: 'Not assessed' },
        diastolicBP: 0,
        heartRate: 0,
        oxygenSaturation: 0,
        respiratoryRate: 0,
        systolicBP: 0,
        diastolicBPUta: false,
        heartRateUta: false,
        oxygenSaturationUta: false,
        respiratoryRateUta: false,
        systolicBPUta: false,
        performedExam: false,
        performedInterview: false,
      };
      
      console.log('Creating encounter with data:', encounterData);
      
      await createEncounter(encounterData);
      
      // Reset form
      setNewEncounterData({
        patientAge: '',
        patientBiologicalSex: '',
        patientGender: '',
        patientPronouns: '',
        ageUnknown: false,
        biologicalSexUnknown: false,
        chiefComplaint: '',
        primaryImpression: ''
      });
      
      // Refetch encounters to update the list
      await refetchEncounters();
      
      toast({
        title: 'Patient encounter created successfully',
        status: 'success',
        duration: 3000,
      });
      onNewEncounterClose();
    } catch (error) {
      console.error('Error creating encounter:', error);
      toast({
        title: 'Error creating encounter',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Format date/time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'Not set';
    const date = new Date(dateTime);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${month} ${day} ${year} ${hours}${minutes}`;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'blue',
      in_progress: 'yellow',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'gray';
  };

  const getCriticalityColor = (criticality) => {
    const colors = {
      green_ambulatory: 'green',
      yellow_not_life_threatening: 'yellow',
      red_critical: 'red',
      black_dead_on_arrival: 'black',
    };
    return colors[criticality] || 'gray';
  };

  return (
    <Box bg={bg} minH="100vh">
      <Container maxW="container.xl" py={8}>

        {/* Header Section */}
        <Card mb={8} bg={cardBg} borderColor={borderColor}>
          <CardHeader>
            <VStack align="start" spacing={-1} width="100%" lineHeight="1.2">
              {/* Top row - Student name in tiny font */}
              <Text fontSize="xs" color="gray.500">
                Student: {shift.student.firstName} {shift.student.lastName}
              </Text>
              
              {/* Main heading row - Shift Details with action buttons */}
              <Flex alignItems="center" justifyContent="space-between" width="100%">
                <Heading size="lg">Shift Details</Heading>
                
                {/* Action buttons on the right */}
                <HStack spacing={2} alignItems="center" height="40px">
                  {canLockUnlock && (
                    <Box position="relative">
                      <IconButton
                        icon={
                          <Flex alignItems="center" justifyContent="center" width="100%" height="100%">
                            <Icon 
                              as={isLockHovered ? (shift.locked ? FiUnlock : FiLock) : (shift.locked ? FiLock : FiUnlock)} 
                              boxSize="16px" 
                              strokeWidth={2.5}
                              transform="translateY(0px)"
                            />
                          </Flex>
                        }
                        aria-label={shift.locked ? 'Unlock shift' : 'Lock shift'}
                        title={shift.locked ? 'Click to unlock this shift - students will be able to edit again' : 'Click to lock this shift - students will no longer be able to edit'}
                        bg="blue.500"
                        color="white"
                        _hover={{ bg: "blue.600", transform: "scale(1.1)" }}
                        _active={{ bg: "blue.700" }}
                        transition="all 0.2s ease"
                        size="sm"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        onClick={handleLockToggle}
                        isLoading={updatingShift}
                        onMouseEnter={() => setIsLockHovered(true)}
                        onMouseLeave={() => setIsLockHovered(false)}
                        width="32px"
                        height="32px"
                        minW="32px"
                        minH="32px"
                        padding={0}
                      />
                      {shift.unlockRequested && shift.unlockRequestStatus === 'pending' && (
                        <Box
                          position="absolute"
                          top="-2px"
                          right="-2px"
                          fontSize="10px"
                          zIndex={1}
                          bg="white"
                          borderRadius="full"
                          p="1px"
                        >
                          ✋
                        </Box>
                      )}
                    </Box>
                  )}
                  {canEdit && (
                    <IconButton
                      icon={
                        <Flex alignItems="center" justifyContent="center" width="100%" height="100%">
                          <Icon as={FiEdit} boxSize="14px" transform="translateY(0px)" />
                        </Flex>
                      }
                      aria-label="Edit shift"
                      bg="blue.500"
                      color="white"
                      _hover={{ bg: "blue.600" }}
                      _active={{ bg: "blue.700" }}
                      size="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      onClick={onEditShiftOpen}
                      width="32px"
                      height="32px"
                      minW="32px"
                      minH="32px"
                      padding={0}
                    />
                  )}
                  {canEdit && (
                    <IconButton
                      icon={
                        <Flex alignItems="center" justifyContent="center" width="100%" height="100%">
                          <Icon as={FiTrash2} boxSize="14px" transform="translateY(0px)" />
                        </Flex>
                      }
                      aria-label="Delete shift"
                      bg="red.500"
                      color="white"
                      _hover={{ bg: "red.600" }}
                      _active={{ bg: "red.700" }}
                      size="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      onClick={onDeleteOpen}
                      onMouseEnter={() => setIsDeleteHovered(true)}
                      onMouseLeave={() => setIsDeleteHovered(false)}
                      width="32px"
                      height="32px"
                      minW="32px"
                      minH="32px"
                      padding={0}
                    />
                  )}
                </HStack>
              </Flex>
              
              {/* Shift details row - all the icons and info */}
              <HStack spacing={4} divider={<Text color="gray.300">•</Text>} fontSize="sm" color="gray.600" alignItems="center" minHeight="20px">
                <Flex alignItems="center" spacing={1}>
                  <Icon as={FiCalendar} color="blue.500" boxSize="14px" transform="translateY(-8px)" />
                  <Text ml={1}>{formatDateTime(shift.shiftDate)}</Text>
                </Flex>
                <Flex alignItems="center" spacing={1}>
                  <Icon as={FiClock} color="green.500" boxSize="14px" transform="translateY(-8px)" />
                  <Text ml={1}>End: {formatDateTime(shift.endTime)}</Text>
                </Flex>
                <Flex alignItems="center" spacing={1}>
                  <Icon as={FiMapPin} color="red.500" boxSize="14px" transform="translateY(-8px)" />
                  <Text ml={1}>{shift.location} - {shift.unit}</Text>
                </Flex>
                <Flex alignItems="center" spacing={1}>
                  <Text fontSize="14px" transform="translateY(-8px)" mr={1}>
                    {getShiftTypeIcon(shift.shiftType)}
                  </Text>
                  <Text ml={1}>{shift.shiftType?.replace('_', ' ').toUpperCase()}</Text>
                </Flex>
              </HStack>
              
              {/* Preceptor row - under the details */}
              <Box>
                <PreceptorSelector 
                  shift={shift} 
                  user={user} 
                  canEdit={canEdit} 
                  onPreceptorChange={handlePreceptorChange}
                />
              </Box>
            </VStack>
          </CardHeader>
          <CardBody>
            {/* Objectives */}
            {shift.objectives?.markdown && (
              <Box>
                <Heading size="sm" mb={2}>Objectives</Heading>
                <Text>{shift.objectives.markdown}</Text>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Main Content Tabs */}
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>
              <Icon as={FiActivity} mr={2} />
              Patient Encounters ({encounters?.length || 0})
            </Tab>
            <Tab>
              <Icon as={FiUpload} mr={2} />
              Documents
            </Tab>
            <Tab>
              <Icon as={FiUserCheck} mr={2} />
              Evaluations
            </Tab>
          </TabList>

          <TabPanels>
            {/* Patient Encounters Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                <HStack justifyContent="space-between">
                  <Heading size="md">Patient Encounters</Heading>
                  <Button
                    leftIcon={<Icon as={FiPlus} />}
                    colorScheme="blue"
                    onClick={onNewEncounterOpen}
                    disabled={shift.locked}
                  >
                    Add New Encounter
                  </Button>
                </HStack>

                {encountersFetching ? (
                  <VStack spacing={4}>
                    <Spinner />
                    <Text>Loading encounters...</Text>
                  </VStack>
                ) : encounters?.length > 0 ? (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {encounters.map((encounter) => (
                      <Card key={encounter.id} variant="outline" cursor="pointer" _hover={{ shadow: 'md' }}>
                        <CardBody>
                          <VStack align="start" spacing={3}>
                            <HStack justifyContent="space-between" width="100%">
                              <Badge colorScheme={getCriticalityColor(encounter.patientCriticality)}>
                                {encounter.patientCriticality?.replace('_', ' ').toUpperCase()}
                              </Badge>
                              <Badge colorScheme="blue">
                                {encounter.reviewStatus?.replace('_', ' ').toUpperCase()}
                              </Badge>
                            </HStack>
                            <Text fontWeight="bold" noOfLines={2}>
                              {encounter.chiefComplaint?.markdown || 'No chief complaint'}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              <strong>Primary:</strong> {encounter.primaryImpression?.replace('_', ' ').toUpperCase()}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              <strong>Patient:</strong> {encounter.patientAge}y {encounter.patientGender}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {formatDateTime(encounter.encounterDate)}
                            </Text>
                            <HStack spacing={2}>
                              <IconButton
                                icon={<Icon as={FiEye} />}
                                size="sm"
                                aria-label="View encounter"
                                onClick={() => navigate(`/patient-encounters/${encounter.id}`)}
                              />
                              <IconButton
                                icon={<Icon as={FiEdit} />}
                                size="sm"
                                aria-label="Edit encounter"
                                onClick={() => navigate(`/patient-encounters/${encounter.id}/edit`)}
                                disabled={shift.locked}
                              />
                            </HStack>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Card variant="outline">
                    <CardBody>
                      <VStack spacing={4}>
                        <Icon as={FiActivity} size="48px" color="gray.400" />
                        <Text color="gray.500">No patient encounters recorded yet</Text>
                        <Button
                          leftIcon={<Icon as={FiPlus} />}
                          colorScheme="blue"
                          onClick={onNewEncounterOpen}
                          disabled={shift.locked}
                        >
                          Add First Encounter
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                )}
              </VStack>
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Heading size="md">Shift Documents</Heading>
                
                {/* Upload Area */}
                <Card variant="outline" borderStyle="dashed" borderWidth="2px">
                  <CardBody>
                    <VStack spacing={4}>
                      <Icon as={FiUpload} size="48px" color="gray.400" />
                      <Text color="gray.500">Drag and drop files here or click to browse</Text>
                      <Text fontSize="sm" color="gray.400">
                        Supported formats: PDF, DOC, DOCX, JPG, PNG, ECG files
                      </Text>
                      <Button colorScheme="blue" leftIcon={<Icon as={FiUpload} />} disabled={shift.locked}>
                        Browse Files
                      </Button>
                      {shift.locked && (
                        <Text fontSize="sm" color="red.500">
                          Shift is locked - cannot upload files
                        </Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                {/* Form Submissions */}
                {formSubmissions && formSubmissions.length > 0 && (
                  <Card variant="outline">
                    <CardHeader>
                      <Heading size="sm">Form Submissions</Heading>
                    </CardHeader>
                    <CardBody>
                      <TableContainer>
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>Form</Th>
                              <Th>Student</Th>
                              <Th>Type</Th>
                              <Th>Status</Th>
                              <Th>Submitted</Th>
                              <Th>Flags</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {formSubmissions.map((sub) => (
                              <Tr key={sub.id}>
                                <Td>{sub.institutionForm?.name || "Unknown"}</Td>
                                <Td>{sub.student ? `${sub.student.firstName} ${sub.student.lastName}` : "—"}</Td>
                                <Td>
                                  <Badge fontSize="2xs" colorScheme="blue">
                                    {sub.submissionType?.replace("_", " ")}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Badge
                                    colorScheme={
                                      sub.status === "verified" ? "green" :
                                      sub.status === "flagged" ? "orange" :
                                      sub.status === "rejected" ? "red" : "gray"
                                    }
                                    fontSize="2xs"
                                  >
                                    {sub.status}
                                  </Badge>
                                </Td>
                                <Td fontSize="xs">{sub.submittedAt ? formatDateTime(sub.submittedAt) : "—"}</Td>
                                <Td>
                                  <HStack spacing={1}>
                                    {sub.variances && Array.isArray(sub.variances) && sub.variances.length > 0 && (
                                      <Badge colorScheme="orange" fontSize="2xs">Variance</Badge>
                                    )}
                                    {sub.phiDetected && (
                                      <Badge colorScheme="red" fontSize="2xs">PHI</Badge>
                                    )}
                                  </HStack>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </CardBody>
                  </Card>
                )}

                {/* Uploaded Files */}
                <Card variant="outline">
                  <CardHeader>
                    <Heading size="sm">Uploaded Files</Heading>
                  </CardHeader>
                  <CardBody>
                    {uploadedFiles.length > 0 ? (
                      <TableContainer>
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>File Name</Th>
                              <Th>Type</Th>
                              <Th>Size</Th>
                              <Th>Uploaded</Th>
                              <Th>Actions</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {uploadedFiles.map((file, index) => (
                              <Tr key={index}>
                                <Td>{file.name}</Td>
                                <Td>{file.type}</Td>
                                <Td>{file.size}</Td>
                                <Td>{file.uploadedAt}</Td>
                                <Td>
                                  <HStack spacing={1}>
                                    <IconButton
                                      icon={<Icon as={FiDownload} />}
                                      size="sm"
                                      aria-label="Download file"
                                    />
                                    <IconButton
                                      icon={<Icon as={FiTrash2} />}
                                      size="sm"
                                      aria-label="Delete file"
                                      colorScheme="red"
                                      disabled={shift.locked}
                                    />
                                  </HStack>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Text color="gray.500">No files uploaded yet</Text>
                    )}
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* Evaluations Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <HStack justifyContent="space-between">
                  <Heading size="md">Shift Evaluations</Heading>
                  {user && shift.student?.id === user.id && (shift.status === 'in_progress' || shift.status === 'completed') && (
                    <StudentEvalQrGenerator
                      shiftId={shift.id}
                      institutionFormId={null}
                      shiftEndTime={shift.endTime}
                    />
                  )}
                </HStack>

                {/* Form Checklist */}
                {(() => {
                  const myDoc = shiftDocs?.find(d => d.studentId === user?.id) || shiftDocs?.[0];
                  const checklist = myDoc?.formChecklist;
                  if (checklist && Array.isArray(checklist) && checklist.length > 0) {
                    return (
                      <Card variant="outline">
                        <CardBody>
                          <FormChecklist checklist={checklist} />
                        </CardBody>
                      </Card>
                    );
                  }
                  return null;
                })()}

                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  {/* Preceptor Evaluation */}
                  <Card variant="outline">
                    <CardHeader>
                      <Heading size="sm">Preceptor Evaluation</Heading>
                    </CardHeader>
                    <CardBody>
                      {shift.preceptor ? (
                        <VStack align="start" spacing={4}>
                          <HStack>
                            <Icon as={FiStar} color="yellow.500" />
                            <Text>Overall Rating: Not yet rated</Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            Student performance evaluation by preceptor
                          </Text>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => navigate(`/preceptor-evaluation/${shift.id}`)}
                          >
                            View/Complete Evaluation
                          </Button>
                        </VStack>
                      ) : (
                        <Text color="gray.500">No preceptor assigned</Text>
                      )}
                    </CardBody>
                  </Card>

                  {/* Student Self-Evaluation */}
                  <Card variant="outline">
                    <CardHeader>
                      <Heading size="sm">Student Self-Evaluation</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack align="start" spacing={4}>
                        <HStack>
                          <Icon as={FiCheckCircle} color="green.500" />
                          <Text>Status: Not completed</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          Reflection on learning objectives and experience
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="green"
                          disabled={shift.locked}
                        >
                          Complete Self-Evaluation
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {/* Performance Metrics */}
                <Card variant="outline">
                  <CardHeader>
                    <Heading size="sm">Performance Metrics</Heading>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
                      <Stat>
                        <StatLabel>Patient Encounters</StatLabel>
                        <StatNumber>{encounters?.length || 0}</StatNumber>
                        <StatHelpText>This shift</StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel>Procedures</StatLabel>
                        <StatNumber>0</StatNumber>
                        <StatHelpText>Completed</StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel>Learning Objectives</StatLabel>
                        <StatNumber>0/0</StatNumber>
                        <StatHelpText>Achieved</StatHelpText>
                      </Stat>
                      <Stat>
                        <StatLabel>Hours Completed</StatLabel>
                        <StatNumber>
                          {shift.shiftDate && shift.endTime 
                            ? Math.round((new Date(shift.endTime) - new Date(shift.shiftDate)) / (1000 * 60 * 60)) 
                            : 0}
                        </StatNumber>
                        <StatHelpText>Hours</StatHelpText>
                      </Stat>
                    </SimpleGrid>
                  </CardBody>
                </Card>

                {/* Feedback and Comments */}
                <Card variant="outline">
                  <CardHeader>
                    <Heading size="sm">Feedback and Comments</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack align="start" spacing={4}>
                      {shift.preceptorNotes?.markdown && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>Preceptor Notes:</Text>
                          <Text>{shift.preceptorNotes.markdown}</Text>
                        </Box>
                      )}
                      {shift.reflectionNotes?.markdown && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>Student Reflection:</Text>
                          <Text>{shift.reflectionNotes.markdown}</Text>
                        </Box>
                      )}
                      {!shift.preceptorNotes?.markdown && !shift.reflectionNotes?.markdown && (
                        <Text color="gray.500">No feedback recorded yet</Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* New Patient Encounter Modal */}
        <Modal isOpen={isNewEncounterOpen} onClose={onNewEncounterClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create New Patient Encounter</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Chief Complaint (Optional)</FormLabel>
                  <Textarea 
                    placeholder="Enter the patient's chief complaint..." 
                    value={newEncounterData.chiefComplaint}
                    onChange={(e) => setNewEncounterData({...newEncounterData, chiefComplaint: e.target.value})}
                  />
                </FormControl>
                
                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>Patient Age</FormLabel>
                    <VStack align="start" spacing={2}>
                      <NumberInput 
                        min={0} 
                        max={120} 
                        value={newEncounterData.patientAge}
                        onChange={(valueStr) => setNewEncounterData({...newEncounterData, patientAge: valueStr})}
                        isDisabled={newEncounterData.ageUnknown}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Checkbox
                        isChecked={newEncounterData.ageUnknown}
                        onChange={(e) => setNewEncounterData({...newEncounterData, ageUnknown: e.target.checked, patientAge: e.target.checked ? '' : newEncounterData.patientAge})}
                      >
                        Age Unknown
                      </Checkbox>
                    </VStack>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Biological Sex</FormLabel>
                    <VStack align="start" spacing={2}>
                      <Select 
                        placeholder="Select biological sex"
                        value={newEncounterData.patientBiologicalSex}
                        onChange={(e) => setNewEncounterData({...newEncounterData, patientBiologicalSex: e.target.value})}
                        isDisabled={newEncounterData.biologicalSexUnknown}
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="intersex">Intersex</option>
                      </Select>
                      <Checkbox
                        isChecked={newEncounterData.biologicalSexUnknown}
                        onChange={(e) => setNewEncounterData({...newEncounterData, biologicalSexUnknown: e.target.checked, patientBiologicalSex: e.target.checked ? '' : newEncounterData.patientBiologicalSex})}
                      >
                        Biological Sex Unknown
                      </Checkbox>
                    </VStack>
                  </FormControl>
                </SimpleGrid>
                
                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel>Gender Identity (Optional)</FormLabel>
                    <Select 
                      placeholder="Select gender identity"
                      value={newEncounterData.patientGender}
                      onChange={(e) => setNewEncounterData({...newEncounterData, patientGender: e.target.value})}
                    >
                      <option value="man">Man</option>
                      <option value="woman">Woman</option>
                      <option value="non-binary">Non-binary</option>
                      <option value="genderfluid">Genderfluid</option>
                      <option value="agender">Agender</option>
                      <option value="transgender_man">Transgender Man</option>
                      <option value="transgender_woman">Transgender Woman</option>
                      <option value="questioning">Questioning</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Pronouns (Optional)</FormLabel>
                    <Input
                      placeholder="e.g., he/him, she/her, they/them"
                      value={newEncounterData.patientPronouns}
                      onChange={(e) => setNewEncounterData({...newEncounterData, patientPronouns: e.target.value})}
                    />
                  </FormControl>
                </SimpleGrid>
                
                <FormControl>
                  <FormLabel>Primary Impression (Optional)</FormLabel>
                  <Select 
                    placeholder="Select primary impression"
                    value={newEncounterData.primaryImpression}
                    onChange={(e) => setNewEncounterData({...newEncounterData, primaryImpression: e.target.value})}
                  >
                    <option value="abdominal_pain">Abdominal Pain</option>
                    <option value="allergic_reaction">Allergic Reaction</option>
                    <option value="altered_mental_state">Altered Mental State</option>
                    <option value="behavioral_psychiatric">Behavioral/Psychiatric</option>
                    <option value="burns">Burns</option>
                    <option value="cardiac_acute_coronary_syndrome">Cardiac - Acute Coronary Syndrome</option>
                    <option value="cardiac_arrest">Cardiac Arrest</option>
                    <option value="cardiac_other">Cardiac - Other</option>
                    <option value="diabetic_symptoms">Diabetic Symptoms</option>
                    <option value="respiratory">Respiratory</option>
                    <option value="trauma_head">Trauma - Head</option>
                    <option value="trauma_chest">Trauma - Chest</option>
                    <option value="trauma_extremities">Trauma - Extremities</option>
                    <option value="other_medical">Other Medical</option>
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onNewEncounterClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleNewEncounter}
                isLoading={creatingEncounter}
              >
                Create Encounter
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteOpen}
          onClose={onDeleteClose}
          leastDestructiveRef={undefined}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Shift
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to remove this shift? The shift will be removed from the calendar and excluded from reports, but all data is preserved in the system.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button onClick={onDeleteClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDeleteShift}
                  ml={3}
                  isLoading={deletingShift}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Container>
    </Box>
  );
};

export default ShiftDetails;